/** @jsxImportSource @emotion/react */
import React, { FunctionComponentElement, memo } from 'react'
import isPropValid from '@emotion/is-prop-valid'
import { css, withTheme, Interpolation, SerializedStyles } from '@emotion/react'
import styled, { CreateStyledComponent } from '@emotion/styled'
import { CSSInterpolation, FunctionInterpolation } from '@emotion/serialize'
import isFunction from 'lodash/isFunction'
/* if-not-on-site-start */
import isDevelopment from '@signal/utils/env/isDevelopment'
import isStorybook from '@signal/utils/env/isStorybook'
import { hashString } from '@signal/utils/hash-string'
/* if-not-on-site-end */
import { AnyReactComponentType } from '@signal/utils/types'
import { MUIThemeType } from '../types'
import tags from './tags'

type DefaultElementProps = {
  children?: React.ReactNode
  theme: MUIThemeType
}

/* if-not-on-site-start */
const REGEX_EXTRACT_SOURCE = /at (.+)src\/(.+) \(/
const REGEX_REPLACE_INVALID_CHARS = /[\.\/]/g
/* if-not-on-site-end */

const testOmitPropsOnStringTag = isPropValid
const testOmitPropsOnComponent = (key: string) => key !== 'theme'

export const getDefaultShouldForwardProp = (tag: React.ElementType) =>
  typeof tag === 'string' &&
  // 96 is one less than the char code
  // for "a" so this is checking that
  // it's a lowercase character
  tag.charCodeAt(0) > 96
    ? testOmitPropsOnStringTag
    : testOmitPropsOnComponent

//
// this is a wrapper around emotion's css wrapper which provides support for props callbacks within the reusable
// CSS template
//
export function ecss<T = DefaultElementProps>(
  template: TemplateStringsArray,
  ...templateArgs: (
    | CSSInterpolation
    | FunctionInterpolation<T & DefaultElementProps>
  )[]
) {
  let originalArgs: (
    | CSSInterpolation
    | FunctionInterpolation<T & DefaultElementProps>
  )[]
  // emotion will pass the properties to anything which is interpolated, but it doesnt' automatically hanlde
  // calling functions.  According to documentation in the emotion source code:
  // If you want to have a css call based on props, create a function that returns a css call like this:
  //    let dynamicStyle = (props) => ecss`color: ${props.color}`
  // It can be called directly with props or interpolated in a styled call like this:
  //    let SomeComponent = styled('div')`${dynamicStyle}`
  return (props: Partial<T & DefaultElementProps>) => {
    if (!originalArgs) {
      originalArgs = templateArgs
    }
    // the key here is that we will substitute any function with a call to that function, passing the props, and storing
    // the result rather than the original function.  We need to keep the original template (with the function) around
    // so we can do this multiple times (whenever properties change)
    const newTemplateArgs = originalArgs.map(arg => {
      const newArg =
        typeof arg === 'function'
          ? (arg as (
              props: Partial<T & DefaultElementProps>
            ) => CSSInterpolation)(props)
          : arg
      return (newArg as unknown) as CSSInterpolation
    })
    // now that all functions have been resolved, we can let the standard emotion solution do the rest
    return css(template, ...newTemplateArgs)
  }
}

type AttrsParamsType<T> =
  | Partial<T & DefaultElementProps>
  | ((
      props: Partial<T & DefaultElementProps>
    ) => Partial<T & DefaultElementProps>)

type AttrsType<T> = (
  params: AttrsParamsType<T>
) => (
  template: CSSInterpolation | FunctionInterpolation<T> | TemplateStringsArray,
  ...templateArgs: Interpolation<Partial<T & DefaultElementProps>>[]
) => WithComponent<T>

// typescript interface for a function (component) decorated with a "withComponent" call
export interface WithComponent<T> {
  (props: Partial<T & DefaultElementProps>): FunctionComponentElement<{
    css: SerializedStyles
  }>
  _estyledAttrsParamsSeen: AttrsParamsType<T>
  _estyledNative: AnyReactComponentType | string
  _estyledWrappedParent?: AnyReactComponentType | string
  withComponent: (
    NewElement: AnyReactComponentType
  ) => (props: Partial<T & DefaultElementProps>) => React.ReactNode
}

type ElementWithEstyledProps<T> = AnyReactComponentType & {
  _estyledAttrsParamsSeen: AttrsParamsType<T>
  _estyledNative: AnyReactComponentType | string
  _estyledWrappedParent?: AnyReactComponentType | string
}

// typescript interface for a function (component) decorated with an "attrs()" call
interface WithAttrs<T> {
  (
    template:
      | CSSInterpolation
      | FunctionInterpolation<T>
      | TemplateStringsArray,
    ...templateArgs: Interpolation<T & DefaultElementProps>[]
  ): WithComponent<T>
  attrs: AttrsType<T>
}

//
// this is a wrapper around emotion's styled components wrapper which provides support for the .attrs() callback
// the goal is for this to be an "almost drop-in replacement" for @emotion/styled.  It is almost identical to
// emotion's native version except that the types must be defined before you pass the Element to wrap rather
// than right before the template
//
export function estyledBase<T = DefaultElementProps>(
  Element: AnyReactComponentType | string
) {
  // this core function actually creates wrapped components
  function estyledCore(
    Element: AnyReactComponentType | string,
    attrsParamsSeen: AttrsParamsType<T>,
    template:
      | CSSInterpolation
      | FunctionInterpolation<T>
      | TemplateStringsArray,
    ...templateArgs: Interpolation<Partial<T & DefaultElementProps>>[]
  ) {
    let label = ''
    let stackEntry = ''

    /* if-not-on-site-start */
    // attempt to create a nice label for the CSS name from the call stack
    if (
      !process.env.REACT_APP_DISABLE_EMOTION_STYLE_DEBUG &&
      (isDevelopment() || isStorybook())
    ) {
      Error.stackTraceLimit = 20
      const stack = new Error().stack ?? ''
      const splitStack = stack.split('\n')
      const stackIndex = splitStack.findIndex(
        call =>
          call.includes('/src/') &&
          !call.includes('/emotion/estyled') &&
          !call.includes('/emotion/index')
      )
      if (stackIndex !== -1) {
        stackEntry = splitStack[stackIndex]
        const matches = stackEntry.match(REGEX_EXTRACT_SOURCE)
        label = `${(matches ? matches![2] : '').replace(
          REGEX_REPLACE_INVALID_CHARS,
          '-'
        )}-${hashString(stackEntry)}`

        // this log entry can be used to map specific styles to the place in the compiled code where they can be found
        console.log(label, 'is defined', stackEntry)
      }
    }
    /* if-not-on-site-end */

    // @ts-ignore
    const ElementUse = Element._estyledNative || Element

    // create the actual @emotion/styled component from the provided element + template
    const Native = styled((ElementUse as unknown) as AnyReactComponentType, {
      shouldForwardProp: prop => {
        const isWrappingEstyled =
          ElementUse.hasOwnProperty('withComponent') &&
          // @ts-ignore
          ElementUse.withComponent.toString().includes('estyledCore')
        return (
          // @ts-ignore
          getDefaultShouldForwardProp(ElementUse)(prop) &&
          (isWrappingEstyled || !prop.startsWith('$')) &&
          prop !== 'theme' &&
          // eslint-disable-next-line prettier/prettier
          prop !== 'as'
          /* if-not-on-site-start */
          // eslint-disable-next-line prettier/prettier
          && prop.toUpperCase() !== '__EMOTION_LABEL_PLEASE_DO_NOT_USE__'
          /* if-not-on-site-end */
        )
      }
    })(template as TemplateStringsArray, ...templateArgs)

    // return a wrapped version of the component
    const Wrapped = (withTheme(
      memo(
        // @ts-ignore
        React.forwardRef((props: Partial<T & DefaultElementProps>, ref) => {
          // gather up all the properties provided by all attrs() calls up the chain
          const allAttrsParamsSeen = [attrsParamsSeen]
          let ElementCur = Element as ElementWithEstyledProps<T> | undefined
          do {
            if (ElementCur?._estyledAttrsParamsSeen) {
              allAttrsParamsSeen.push(ElementCur?._estyledAttrsParamsSeen)
            }
            ElementCur = ElementCur?._estyledWrappedParent as
              | ElementWithEstyledProps<T>
              | undefined
          } while (ElementCur)

          const attrsProps = allAttrsParamsSeen.reduce(
            (accProps, attrsParamsCur) => ({
              ...accProps,
              ...(isFunction(attrsParamsCur)
                ? attrsParamsCur(props)
                : attrsParamsCur)
            }),
            {}
          )

          let debugProps = {}
          /* if-not-on-site-start */
          debugProps = label
            ? {
                // @ts-ignore
                css: props.css || {}, // important to set this for label to be used
                __EMOTION_LABEL_PLEASE_DO_NOT_USE__: label
              }
            : {}
          /* if-not-on-site-end */

          return <Native {...props} {...attrsProps} {...debugProps} ref={ref} />
        })
      )
    ) as unknown) as WithComponent<T>

    // shim in a new withComponent implementation, which is aware of attrs
    Wrapped.withComponent = (NewElement: AnyReactComponentType) => {
      return (estyledCore(
        NewElement,
        attrsParamsSeen,
        template,
        ...templateArgs
      ) as unknown) as (
        props: Partial<T & DefaultElementProps>
      ) => React.ReactNode
    }

    Wrapped._estyledNative = Native
    Wrapped._estyledWrappedParent = Element
    Wrapped._estyledAttrsParamsSeen = attrsParamsSeen

    return Wrapped
  }

  // return the function that will accept the template string
  const interpolator = (
    template: TemplateStringsArray,
    ...templateArgs: Interpolation<Partial<T & DefaultElementProps>>[]
  ): AnyReactComponentType => {
    // generate a child from this template
    // @ts-ignore
    return estyledCore(
      Element,
      // @ts-ignore
      interpolator._estyledAttrsParamsSeen,
      template,
      ...templateArgs
    )
  }

  // shim in the attrs implementation
  interpolator.attrs = (attrs: AttrsParamsType<T>) => {
    // simply set the attrs callback, which will be used from the component wrapper above
    // @ts-ignore
    interpolator._estyledAttrsParamsSeen = attrs
    return (interpolator as unknown) as WithAttrs<T>
  }

  return (interpolator as unknown) as WithAttrs<T>
}

// code below this point was adapted from emotion to allow us to wrap the estyled method with individual tag types, so you
// can use estyled.a, estyled.div, etc.

type StyledTags = {
  [Tag in keyof JSX.IntrinsicElements]: CreateStyledComponent<
    {
      theme?: MUIThemeType
      as?: React.ElementType
    },
    JSX.IntrinsicElements[Tag]
  >
}

interface EStyledInterface {
  <T>(Element: AnyReactComponentType | string): WithAttrs<T>
}

interface EStyledType extends EStyledInterface, StyledTags {}

// bind it to avoid mutating the original function
export const estyled = estyledBase.bind(null) as EStyledType

tags.forEach(tagName => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(estyled as any)[tagName] = estyled(tagName as keyof typeof estyled)
})

export default estyled
